import { useState } from 'react';
import {
    makeStyles,
    tokens,
    Button,
    Spinner,
    Badge,
    Table,
    TableHeader,
    TableHeaderCell,
    TableBody,
    TableRow,
    TableCell,
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogContent,
    DialogActions,
    Dropdown,
    Option,
} from '@fluentui/react-components';
import {
    Add24Regular,
    ArrowSync24Regular,
    Delete20Regular,
    Edit20Regular,
    Checkmark20Regular,
    Dismiss20Regular,
    Open16Regular,
    Info20Regular,
} from '@fluentui/react-icons';
import type { SearchIndexerContextValue } from '../../hooks/useSearchIndexer';
import type { InjioRegistrySource, ListColumn } from '@shared/dto/searchIndexer';
import { RegisterSourceModal } from './RegisterSourceModal';
import type { SourceRegistrationRequest } from '@shared/dto/searchIndexer';

const useStyles = makeStyles({
    card: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
    },
    heading: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        margin: 0,
    },
    actions: {
        display: 'flex',
        gap: '8px',
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px 24px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
    },
    tableWrapper: {
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '::-webkit-scrollbar': {
            display: 'none',
        },
    },
    smallText: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    tagCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    tagEdit: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'nowrap',
    },
    actionsCell: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    detailRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '10px 0',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    detailRowLast: {
        borderBottom: 'none',
    },
    detailLabel: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    detailValue: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
    },
});

const formatDate = (iso?: string) => {
    if (!iso) return <span style={{ color: tokens.colorNeutralForeground4 }}>Never</span>;
    return new Date(iso).toLocaleDateString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric',
    });
};

const typeBadge = (sourceType: InjioRegistrySource['sourceType']) => {
    if (sourceType === 'Page') {
        return <Badge color="informative" appearance="tint">Page Library</Badge>;
    }
    return <Badge appearance="outline">List</Badge>;
};

interface TagEditorProps {
    source: InjioRegistrySource;
    getSourceColumns: (listId: string) => Promise<ListColumn[]>;
    onSave: (source: InjioRegistrySource, fieldName: string) => Promise<void>;
}

function TagEditor({ source, getSourceColumns, onSave }: TagEditorProps) {
    const styles = useStyles();
    const [editing, setEditing] = useState(false);
    const [columns, setColumns] = useState<ListColumn[]>([]);
    const [loadingCols, setLoadingCols] = useState(false);
    const [selected, setSelected] = useState(source.tagFieldName ?? '');
    const [saving, setSaving] = useState(false);

    const handleEdit = async () => {
        setSelected(source.tagFieldName ?? '');
        setLoadingCols(true);
        setEditing(true);
        try {
            const cols = await getSourceColumns(source.listId);
            // Tag fields are Choice/MultiChoice or Lookup columns — filter out other types.
            setColumns(cols.filter(c => c.columnType === 'choice' || c.columnType === 'multiChoice' || c.columnType === 'lookup'));
        } finally {
            setLoadingCols(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(source, selected);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditing(false);
        setSelected(source.tagFieldName ?? '');
    };

    if (!editing) {
        return (
            <div className={styles.tagCell}>
                <span style={{ fontSize: tokens.fontSizeBase200, color: source.tagFieldName ? tokens.colorNeutralForeground1 : tokens.colorNeutralForeground4 }}>
                    {source.tagFieldName ?? '—'}
                </span>
                <Button
                    appearance="subtle"
                    size="small"
                    icon={<Edit20Regular />}
                    onClick={handleEdit}
                    title="Edit tag field"
                />
            </div>
        );
    }

    return (
        <div className={styles.tagEdit}>
            {loadingCols ? (
                <Spinner size="tiny" />
            ) : (
                <Dropdown
                    size="small"
                    style={{ minWidth: '150px' }}
                    value={(columns.find(c => c.name === selected)?.displayName ?? selected) || 'None'}
                    onOptionSelect={(_, d) => setSelected(d.optionValue as string)}
                >
                    <Option value="">None</Option>
                    {columns.map(c => (
                        <Option key={c.name} value={c.name}>{c.displayName}</Option>
                    ))}
                </Dropdown>
            )}
            <Button
                appearance="subtle"
                size="small"
                icon={saving ? <Spinner size="tiny" /> : <Checkmark20Regular />}
                onClick={handleSave}
                disabled={saving || loadingCols}
                title="Save"
            />
            <Button
                appearance="subtle"
                size="small"
                icon={<Dismiss20Regular />}
                onClick={handleCancel}
                disabled={saving}
                title="Cancel"
            />
        </div>
    );
}

interface Props {
    ctx: SearchIndexerContextValue;
}

export function SourcesPanel({ ctx }: Props) {
    const styles = useStyles();
    const {
        sources,
        sourcesLoading,
        registryExists,
        registerSource,
        deleteSource,
        getSiteLists,
        getSourceColumns,
        updateSourceTagField,
        loadSources,
    } = ctx;

    const [modalOpen, setModalOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<InjioRegistrySource | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [detailsSource, setDetailsSource] = useState<InjioRegistrySource | null>(null);

    const handleRegister = async (config: SourceRegistrationRequest) => {
        await registerSource(config);
        setModalOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await deleteSource(confirmDelete);
            setConfirmDelete(null);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className={styles.card}>
            <div className={styles.topBar}>
                <h3 className={styles.heading}>Registered Sources</h3>
                <div className={styles.actions}>
                    <Button
                        icon={<ArrowSync24Regular />}
                        appearance="subtle"
                        onClick={loadSources}
                        disabled={sourcesLoading}
                    >
                        Refresh
                    </Button>
                    <Button
                        icon={<Add24Regular />}
                        appearance="primary"
                        onClick={() => setModalOpen(true)}
                        disabled={!registryExists}
                    >
                        Register Source
                    </Button>
                </div>
            </div>

            {sourcesLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                    <Spinner label="Loading sources…" />
                </div>
            ) : sources.length === 0 ? (
                <div className={styles.emptyState}>
                    {registryExists
                        ? 'No sources registered yet. Click "Register Source" to add one.'
                        : 'Registry not found on this site. Complete onboarding to set up the registry.'}
                </div>
            ) : (
                <div className={styles.tableWrapper}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell style={{ width: '220px' }}>Source</TableHeaderCell>
                                <TableHeaderCell style={{ width: '110px' }}>Type</TableHeaderCell>
                                <TableHeaderCell style={{ width: '260px' }}>Tag Field</TableHeaderCell>
                                <TableHeaderCell style={{ width: '120px' }}>Last Synced</TableHeaderCell>
                                <TableHeaderCell style={{ width: '110px' }}>Status</TableHeaderCell>
                                <TableHeaderCell style={{ width: '60px' }} />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sources.map(source => (
                                <TableRow key={source.listId}>
                                    <TableCell>
                                        <div>
                                            {source.sourceListUrl ? (
                                                <a
                                                    href={source.sourceListUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title="Open this list/library in SharePoint"
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                        color: tokens.colorBrandForeground1,
                                                        textDecoration: 'none',
                                                    }}
                                                    onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                                                    onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                                                >
                                                    <strong>{source.displayName ?? source.listId}</strong>
                                                    <Open16Regular style={{ width: 14, height: 14, flexShrink: 0 }} />
                                                </a>
                                            ) : (
                                                <strong>{source.displayName ?? source.listId}</strong>
                                            )}
                                            <div className={styles.smallText} style={{ marginTop: '2px' }}>
                                                {source.listId}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {typeBadge(source.sourceType)}
                                    </TableCell>
                                    <TableCell>
                                        <TagEditor
                                            source={source}
                                            getSourceColumns={getSourceColumns}
                                            onSave={updateSourceTagField}
                                        />
                                    </TableCell>
                                    <TableCell className={styles.smallText}>
                                        {formatDate(source.lastFullSyncDate)}
                                    </TableCell>
                                    <TableCell>
                                        {source.lockOwner &&
                                        source.lockExpiresAt &&
                                        new Date(source.lockExpiresAt) > new Date() ? (
                                            <Badge color="warning" appearance="filled">Indexing</Badge>
                                        ) : source.lastFullSyncDate ? (
                                            <Badge color="success" appearance="filled">Indexed</Badge>
                                        ) : (
                                            <Badge appearance="outline">Not indexed</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className={styles.actionsCell}>
                                            <Button
                                                appearance="subtle"
                                                size="small"
                                                icon={<Info20Regular />}
                                                onClick={() => setDetailsSource(source)}
                                                title="View details"
                                            />
                                            <Button
                                                appearance="subtle"
                                                size="small"
                                                icon={<Delete20Regular />}
                                                onClick={() => setConfirmDelete(source)}
                                                title="Delete source"
                                                style={{ color: tokens.colorPaletteRedForeground1 }}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Delete confirmation dialog */}
            <Dialog
                open={confirmDelete !== null}
                onOpenChange={(_, d) => { if (!d.open && !deleting) setConfirmDelete(null); }}
            >
                <DialogSurface>
                    <DialogTitle>Remove Source</DialogTitle>
                    <DialogBody>
                        <DialogContent>
                            Remove <strong>{confirmDelete?.displayName ?? confirmDelete?.listId}</strong> from
                            the search registry? Existing embeddings in its cache list will remain but will
                            no longer be updated by the indexer.
                        </DialogContent>
                        <DialogActions>
                            <Button
                                appearance="subtle"
                                onClick={() => setConfirmDelete(null)}
                                disabled={deleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                                icon={deleting ? <Spinner size="tiny" /> : <Delete20Regular />}
                                style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                            >
                                {deleting ? 'Removing…' : 'Remove'}
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            {/* Source details dialog */}
            <Dialog
                open={detailsSource !== null}
                onOpenChange={(_, d) => { if (!d.open) setDetailsSource(null); }}
            >
                <DialogSurface>
                    <DialogTitle>{detailsSource?.displayName ?? detailsSource?.listId}</DialogTitle>
                    <DialogBody>
                        <DialogContent>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Filter</span>
                                <span className={styles.detailValue}>
                                    {detailsSource?.filterFieldName
                                        ? `${detailsSource.filterFieldName} ${detailsSource.filterOperator ?? 'Equals'} "${detailsSource.filterValue ?? ''}"`
                                        : 'No filter configured'}
                                </span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Detail Page URL Template</span>
                                <span className={styles.detailValue}>
                                    {detailsSource?.detailPageUrlTemplate || '—'}
                                </span>
                            </div>
                            <div className={styles.detailRow}>
                                <span className={styles.detailLabel}>Embeddings cache list</span>
                                <span className={styles.detailValue}>
                                    {detailsSource?.cacheListUrl ? (
                                        <a
                                            href={detailsSource.cacheListUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                color: tokens.colorBrandForeground1,
                                                textDecoration: 'none',
                                            }}
                                        >
                                            Open in SharePoint
                                            <Open16Regular style={{ width: 14, height: 14 }} />
                                        </a>
                                    ) : '—'}
                                </span>
                                <span className={styles.smallText}>{detailsSource?.cacheListId}</span>
                            </div>
                            <div className={`${styles.detailRow} ${styles.detailRowLast}`}>
                                <span className={styles.detailLabel}>Lock status</span>
                                <span className={styles.detailValue}>
                                    {detailsSource?.lockOwner &&
                                    detailsSource.lockExpiresAt &&
                                    new Date(detailsSource.lockExpiresAt) > new Date()
                                        ? `Locked by ${detailsSource.lockOwner} until ${new Date(detailsSource.lockExpiresAt).toLocaleString()}`
                                        : 'Not locked'}
                                </span>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="secondary" onClick={() => setDetailsSource(null)}>
                                Close
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            <RegisterSourceModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onRegister={handleRegister}
                getSiteLists={getSiteLists}
                getSourceColumns={getSourceColumns}
            />
        </div>
    );
}
